import { Alert, App, Button, Tooltip } from 'antd';
import clsx from 'clsx';
import type { MouseEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AntivirusScanStatusEnum } from '../../../enums/AntivirusScanStatusEnum';
import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import { useAuth } from '../../../hooks/useAuth';
import type File from '../../../interfaces/File';
import { FileService } from '../../../services/fileApiService';
import { useAppStore } from '../../../store/store';
import { getApiErrorCode } from '../../../utils/apiErrorCode';
import {
  getAntivirusErrorMessage,
  getAntivirusUiState,
  type AntivirusUiKind,
} from '../../../utils/antivirus';
import { Icon } from '../../Core/Icon';
import type { iconComponents } from '../../Core/IconImports';

const fileService = new FileService();

interface FileAntivirusStatusIconProps {
  file: File;
  /** Compact icon for table/grid; banner for detail panel header. */
  variant?: 'icon' | 'banner';
}

function iconForKind(kind: AntivirusUiKind): keyof typeof iconComponents {
  switch (kind) {
    case 'pending':
      // Shield family (not LoaderIcon) — avoid looking like generic load / AI classify.
      return 'ShieldEllipsisIcon';
    case 'tooLarge':
      return 'ShieldOffIcon';
    case 'error':
      return 'ShieldAlertIcon';
    case 'infected':
      return 'ShieldBanIcon';
    case 'rescanPending':
    case 'rescanFailed':
      return 'ShieldAlertIcon';
    default:
      return 'ShieldAlertIcon';
  }
}

function iconClassForKind(kind: AntivirusUiKind): string {
  switch (kind) {
    case 'pending':
      return 'text-blue-dark h-5 w-5 shrink-0 animate-pulse';
    case 'tooLarge':
      return 'text-zinc h-5 w-5 shrink-0';
    case 'error':
      return 'text-yellow-dark h-5 w-5 shrink-0';
    case 'infected':
    case 'rescanPending':
    case 'rescanFailed':
      return 'text-red-dark h-5 w-5 shrink-0';
    default:
      return 'text-zinc h-5 w-5 shrink-0';
  }
}

function alertTypeForKind(kind: AntivirusUiKind): 'info' | 'warning' | 'error' {
  if (kind === 'pending' || kind === 'tooLarge') return 'info';
  if (kind === 'error') return 'warning';
  return 'error';
}

const FileAntivirusStatusIcon = ({ file, variant = 'icon' }: FileAntivirusStatusIconProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { authenticatedUser } = useAuth();
  const [retrying, setRetrying] = useState(false);

  if (!authenticatedUser?.isAntivirusEnabled || file.isFolder) {
    return null;
  }

  const ui = getAntivirusUiState(file);
  if (ui.kind === 'none') {
    return null;
  }

  const message =
    ui.kind === 'error'
      ? getAntivirusErrorMessage(t, ui.errorCode)
      : ui.kind === 'tooLarge'
        ? t('file.antivirus.tooLarge')
        : t(`file.antivirus.${ui.kind}` as const, { threatName: ui.threatName });
  const stickyNote =
    ui.kind === 'infected' || ui.kind === 'rescanPending' || ui.kind === 'rescanFailed'
      ? t('file.antivirus.stickyNote')
      : null;
  const contactAdmin = ui.blocked && ui.kind !== 'error' ? t('file.antivirus.contactAdmin') : null;

  const handleRetry = async (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (retrying || !ui.showOwnerRetry) return;

    setRetrying(true);
    try {
      await fileService.requestAntivirusRescan(file.id);
      useAppStore.getState().events.emit.onFilesUpdated({
        files: [
          {
            id: file.id,
            avScanStatus: AntivirusScanStatusEnum.Pending,
            avThreatName: '',
            avScanError: '',
          },
        ],
      });
      notification.info({ title: t('file.antivirus.pending') });
    } catch (error: unknown) {
      const errResponse = (error as { response?: unknown })?.response;
      const code = getApiErrorCode(errResponse);
      if (code === ApiErrorEnum.AntivirusRescanNotAllowed) {
        notification.error({ title: t('errors.antivirusRescanNotAllowed') });
      } else if (code === ApiErrorEnum.AntivirusNotAvailable) {
        notification.error({ title: t('errors.antivirusNotAvailable') });
      } else {
        notification.error({ title: t('errors.genericError') });
      }
    } finally {
      setRetrying(false);
    }
  };

  const tooltipContent = (
    <div className="flex max-w-[18rem] flex-col gap-1.5 text-left">
      <span className="text-sm leading-snug">{message}</span>
      {ui.threatName ? (
        <span className="text-xs leading-snug opacity-90">{ui.threatName}</span>
      ) : null}
      {stickyNote ? <span className="text-xs leading-snug opacity-90">{stickyNote}</span> : null}
      {contactAdmin ? (
        <span className="text-xs leading-snug opacity-90">{contactAdmin}</span>
      ) : null}
      {ui.showOwnerRetry ? (
        <Button
          type="link"
          size="small"
          className="h-auto self-start p-0! text-left"
          loading={retrying}
          onClick={handleRetry}
        >
          {t('file.antivirus.retry')}
        </Button>
      ) : null}
    </div>
  );

  if (variant === 'banner') {
    return (
      <Alert
        type={alertTypeForKind(ui.kind)}
        showIcon
        className="mb-2"
        message={message}
        description={
          <div className="flex flex-col gap-1.5">
            {ui.threatName ? <span>{ui.threatName}</span> : null}
            {stickyNote ? <span>{stickyNote}</span> : null}
            {contactAdmin ? <span>{contactAdmin}</span> : null}
            {ui.showOwnerRetry ? (
              <Button size="small" className="self-start" loading={retrying} onClick={handleRetry}>
                {t('file.antivirus.retry')}
              </Button>
            ) : null}
          </div>
        }
      />
    );
  }

  // Icon + tooltip only in list/grid; title/Retry live in detail banner.
  return (
    <Tooltip
      title={tooltipContent}
      styles={{ container: { paddingInline: 12, paddingBlock: 10, maxWidth: 320 } }}
    >
      <div
        className={clsx('inline-flex items-center', ui.showOwnerRetry && 'cursor-pointer')}
        aria-label={message}
        onClick={ui.showOwnerRetry ? handleRetry : undefined}
        role={ui.showOwnerRetry ? 'button' : undefined}
        tabIndex={ui.showOwnerRetry ? 0 : undefined}
        onKeyDown={
          ui.showOwnerRetry
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  void handleRetry(event as unknown as MouseEvent);
                }
              }
            : undefined
        }
      >
        <Icon name={iconForKind(ui.kind)} className={iconClassForKind(ui.kind)} />
      </div>
    </Tooltip>
  );
};

export default FileAntivirusStatusIcon;
